/**
 * Decide when a layer should use Giro3D's true-3D terrain pipeline:
 *
 *   COG DEM → ElevationLayer → terrain mesh → ColorLayer drape → orbit camera
 *
 * That is the ArcGIS Pro–style relief path (gap G20). Flat ColorLayer tiles
 * stay for imagery and for DEM *derivatives* (hillshade / slope / aspect),
 * which are already 8-bit products and must not be treated as height fields.
 */

import type { Layer } from '@geosoft/viewer-sdk';
import { TERRAIN_ELEVATION_MESH } from '@/lib/featureFlags';
import { terrainDerivativeColorRole } from '@/utils/titilerDemColorize';

type MeshLayer = Pick<Layer, 'id' | 'kind' | 'label' | 'style' | 'endpoints'>;

/** Names that mean "this band is a height above a datum". */
const ELEVATION_NAME = /(^|[^a-z])(dem|dtm|dsm|elevation|hypsometr|terrain)([^a-z]|$)/;

/**
 * Products *derived* from a DEM. They are 8-bit visualisations, so feeding them
 * to an ElevationLayer would build a mesh out of shading values.
 */
const DERIVATIVE_NAME = /(hillshade|slope|aspect|ndsm|contour|rastercalc)/;

function styleText(layer: MeshLayer, key: string): string {
  const value = (layer.style as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' ? value : '';
}

function nameBlob(layer: MeshLayer): string {
  return [
    layer.id,
    layer.label ?? '',
    styleText(layer, 'title'),
    styleText(layer, 'family'),
    styleText(layer, 'artifactKind'),
    styleText(layer, 'gtwinArtifactKind'),
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * True DEM / DTM / DSM elevation rasters — not hillshade/slope/aspect companions.
 * These carry real heights and can drive an ElevationLayer mesh in Giro3D.
 *
 * Deliberately stricter than `isDemLikeLayer`, which also answers true for *any*
 * single-band TiTiler URL (`bidx=1`). That fallback is right for choosing a
 * greyscale stretch, but turning every single-band raster — NDVI, landcover, a
 * mask — into a height field is not: geometry needs positive evidence that the
 * band is an elevation.
 */
export function isElevationDemRaster(layer: MeshLayer): boolean {
  if (layer.kind !== 'raster') return false;
  if (terrainDerivativeColorRole(layer)) return false;
  const blob = nameBlob(layer);
  if (DERIVATIVE_NAME.test(blob)) return false;
  const family = styleText(layer, 'family').toLowerCase();
  if (family === 'terrain' || family === 'dem' || family === 'elevation') return true;
  return ELEVATION_NAME.test(blob);
}

/**
 * Should `describeGiro3DLayer` route this layer to `terrain-source`
 * (Giro3D ElevationLayer) instead of a flat ColorLayer?
 *
 * - `kind: 'terrain'` — still gated by `VITE_FEATURE_TERRAIN_ELEVATION_MESH`
 *   (existing spike; default OFF until that rollout is closed).
 * - DEM-like rasters (DTM/DEM COGs such as Turkey DTM) — always yes in Giro3D,
 *   so switching to "3D view" produces real relief without an env flag.
 */
export function shouldUseElevationMesh(layer: MeshLayer): boolean {
  if (layer.kind === 'terrain') return TERRAIN_ELEVATION_MESH;
  return isElevationDemRaster(layer);
}

/** TiTiler template URL to feed the ElevationLayer float-tile path. */
export function elevationMeshSourceUrl(layer: MeshLayer): string | undefined {
  const endpoints = layer.endpoints as { terrain?: string; tiles?: string } | undefined;
  return endpoints?.terrain ?? endpoints?.tiles;
}
